/**
 * CancelPage — public /cancel/:token route (iter114).
 *
 * Flow:
 *   1) GET /api/demo/bookings/cancel/{token} — returns booking summary
 *      (with `already_cancelled` flag for idempotency).
 *   2) One-click "Cancel this demo" confirmation → POST same URL.
 *   3) Success screen with a warm "Book a new demo" CTA.
 *
 * Uses the same signed HMAC token family as ReschedulePage — one token
 * covers both actions per booking, expiring 1h before the demo.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Calendar, Check, Loader2, Globe, AlertTriangle, XCircle, ArrowRight,
} from 'lucide-react';
import LandingNav from '@/components/landing/LandingNav';
import BookDemoModal from '@/components/landing/BookDemoModal';
import DotboxLogo from '@/components/common/DotboxLogo';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CenterShell = ({ children, onOpenBookDemo }) => (
  <div className="min-h-screen bg-[#0A1024] text-slate-200 flex flex-col">
    <LandingNav onOpenBookDemo={onOpenBookDemo || (() => {})} />
    <main className="flex-1 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
    </main>
    <footer className="bg-[#0A1024] border-t border-[#1F2A4D] py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <DotboxLogo variant="dark" className="h-8" />
        <div className="flex items-center gap-3">
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' })}
            className="text-slate-400 hover:text-rose-400 text-sm"
            data-testid="cancel-footer-home"
          >← Back to home</Link>
          <span className="text-slate-600">·</span>
          <p className="text-slate-500 text-sm">©2026 dotbox.ai — All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
);

const CancelPage = () => {
  const { token } = useParams();
  const [showBookDemo, setShowBookDemo] = useState(false);
  // States: loading | confirm | already_cancelled | success | expired | bad | notfound | error
  const [state, setState] = useState('loading');
  const [booking, setBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const fetchState = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/demo/bookings/cancel/${token}`);
      const b = r.data.booking;
      setBooking(b);
      setState(b.already_cancelled ? 'already_cancelled' : 'confirm');
    } catch (e) {
      const code = e?.response?.status;
      if (code === 410) setState('expired');
      else if (code === 401) setState('bad');
      else if (code === 404) setState('notfound');
      else setState('error');
    }
  }, [token]);

  useEffect(() => {
    if (!token) { setState('bad'); return; }
    fetchState();
  }, [token, fetchState]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/demo/bookings/cancel/${token}`);
      setConfirmation(r.data);
      setState('success');
      toast.success('Your demo has been cancelled.');
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 410) setState('expired');
      else if (code === 401) setState('bad');
      else if (code === 404) setState('notfound');
      else toast.error(typeof detail === 'string' ? detail : 'Cancel failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Loading ----
  if (state === 'loading') {
    return (
      <CenterShell>
        <div className="flex items-center gap-2 text-slate-400 py-16 justify-center" data-testid="cancel-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Verifying your cancellation link…
        </div>
      </CenterShell>
    );
  }

  // ---- Error / boundary states ----
  if (state === 'expired' || state === 'bad' || state === 'notfound' || state === 'error') {
    const isExpired = state === 'expired';
    const isNotFound = state === 'notfound';
    return (
      <CenterShell onOpenBookDemo={() => setShowBookDemo(true)}>
        <Card className="border border-amber-500/40" data-testid={`cancel-${state}`}>
          <CardContent className="p-8">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              <h1 className="text-lg font-semibold text-white">
                {isExpired
                  ? "This cancellation link has expired."
                  : isNotFound
                    ? "We couldn't find that booking."
                    : "We couldn't verify that cancellation link."}
              </h1>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-6">
              {isExpired
                ? "Cancellation links stay active until one hour before the meeting. If you still need to cancel, please reply to your confirmation email and we'll take care of it."
                : "The link may be malformed, or the booking may already have been cancelled from a different device."}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => setShowBookDemo(true)}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                data-testid="cancel-fallback-book"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Book a new demo
              </Button>
              <Button asChild variant="outline" className="border-[#1F2A4D] bg-[#0A1024] text-slate-200">
                <Link to="/contact">Contact us instead</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
        <BookDemoModal open={showBookDemo} onOpenChange={setShowBookDemo} />
      </CenterShell>
    );
  }

  // ---- Already cancelled ----
  if (state === 'already_cancelled') {
    return (
      <CenterShell onOpenBookDemo={() => setShowBookDemo(true)}>
        <Card className="border border-slate-500/40" data-testid="cancel-already">
          <CardContent className="p-8">
            <div className="flex flex-col items-center text-center py-4">
              <div className="h-14 w-14 rounded-full bg-slate-500/15 border border-slate-500/40 flex items-center justify-center mb-4">
                <XCircle className="h-7 w-7 text-slate-400" />
              </div>
              <h1 className="text-2xl font-semibold text-white mb-2">This demo is already cancelled.</h1>
              <p className="text-sm text-slate-400 max-w-md">
                No further action is needed — the calendar invite has been withdrawn.
              </p>
            </div>
            {booking && (
              <div className="bg-[#0A1024] border border-[#1F2A4D] rounded-md p-4 space-y-2 mt-6">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-100 font-medium line-through">{booking.current_local_start_display}</span>
                  <span className="text-slate-500">→ {booking.current_local_end_display}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Globe className="h-3.5 w-3.5" /> {booking.timezone}
                </div>
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-3 justify-end">
              <Button
                onClick={() => setShowBookDemo(true)}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                data-testid="cancel-already-book-new"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Book a new demo
              </Button>
              <Button asChild variant="outline" className="border-[#1F2A4D] bg-[#0A1024] text-slate-200">
                <Link to="/">Back to home</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
        <BookDemoModal open={showBookDemo} onOpenChange={setShowBookDemo} />
      </CenterShell>
    );
  }

  // ---- Success ----
  if (state === 'success' && confirmation) {
    return (
      <CenterShell onOpenBookDemo={() => setShowBookDemo(true)}>
        <Card className="border border-emerald-500/40" data-testid="cancel-success">
          <CardContent className="p-8">
            <div className="flex flex-col items-center text-center py-4">
              <div className="h-14 w-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-4">
                <Check className="h-7 w-7 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-semibold text-white mb-2">Demo cancelled — you&apos;re all set.</h1>
              <p className="text-sm text-slate-400 max-w-md">
                We&apos;ve emailed a cancellation confirmation. Your calendar client should remove the event automatically.
              </p>
            </div>
            <div className="bg-[#0A1024] border border-[#1F2A4D] rounded-md p-4 space-y-2 mt-6">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-red-400" />
                <span className="text-slate-100 font-medium line-through">{confirmation.local_start_display}</span>
                <span className="text-slate-500">→ {confirmation.local_end_display}</span>
              </div>
            </div>
            <p className="text-sm text-slate-300 mt-6">
              Would you like to book a new time when you&apos;re ready? We&apos;d still love to walk you through dotbox.ai.
            </p>
            <div className="mt-4 flex flex-wrap gap-3 justify-end">
              <Button
                onClick={() => setShowBookDemo(true)}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                data-testid="cancel-success-book-new"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Book a new demo
              </Button>
              <Button asChild variant="outline" className="border-[#1F2A4D] bg-[#0A1024] text-slate-200">
                <Link to="/">Done</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
        <BookDemoModal open={showBookDemo} onOpenChange={setShowBookDemo} />
      </CenterShell>
    );
  }

  // ---- Confirm ----
  return (
    <CenterShell onOpenBookDemo={() => setShowBookDemo(true)}>
      <div className="mb-6" data-testid="cancel-page">
        <div className="text-xs uppercase tracking-[0.2em] text-red-400 font-semibold mb-2">Cancel demo</div>
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
          Hi {booking?.full_name_first || 'there'} — cancel this demo?
        </h1>
        <p className="text-sm text-slate-400">
          One click below and we&apos;ll withdraw your calendar invite and delete the Zoom meeting.
        </p>
      </div>

      <Card className="border mb-6" data-testid="cancel-current-slot">
        <CardContent className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Currently scheduled</div>
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-red-400" />
            <span className="text-slate-100 font-medium">{booking?.current_local_start_display}</span>
            <span className="text-slate-500">→ {booking?.current_local_end_display}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">{booking?.timezone}</div>
        </CardContent>
      </Card>

      <Card className="border">
        <CardContent className="p-6 space-y-4">
          <div className="text-sm text-slate-300 leading-relaxed">
            If you meant to <strong className="text-white">reschedule</strong> instead, close this page and use the
            &quot;Reschedule&quot; link in your confirmation email — you&apos;ll get to pick a new time without cancelling.
          </div>
          <div className="flex flex-wrap gap-3 justify-end pt-2">
            <Button
              asChild
              variant="outline"
              className="border-[#1F2A4D] bg-[#0A1024] text-slate-200 hover:text-white"
              data-testid="cancel-keep-button"
            >
              <Link to="/">Keep my demo</Link>
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
              data-testid="cancel-confirm-button"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Cancelling…</>
              ) : (
                <>Cancel this demo <ArrowRight className="ml-2 h-4 w-4" /></>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <BookDemoModal open={showBookDemo} onOpenChange={setShowBookDemo} />
    </CenterShell>
  );
};

export default CancelPage;
